import { call, put, select, takeLatest } from 'redux-saga/effects'
import i18n from '../../../../i18n'
import { showToast } from '../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../components/toast/store/toast.constants'
import { getAuthenticationJwtSelector } from '../../../login/selectors/getAuthenticationJwt.selector'
import { QaAddendumService } from '../services/qaAddendum.service'
import { ZollAPIService } from '../../../../services/zollApi.service'
import { setChartSummary } from '../store/qaAddendum.actions'
import { QaAddendumConstants } from '../store/qaAddendum.constants'
import { IQaChartSummary, IQaChartSummaryResponse } from '../store/qaAddendum.types'
import { getQaAddendumSelector } from '../selectors'
import { handleApiError } from '../../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import Logger from '../../../../services/logger.service'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'

const hasChartSummaryPages = (chartSummary: IQaChartSummary | null): boolean =>
  (chartSummary?.flatMap((form) => form.pages ?? []) ?? []).length > 0

const getExistingSummaryForFlag = (
  chartSummaryFlagId: string | null,
  flagId: string,
  chartSummary: IQaChartSummary | null
): IQaChartSummary | null => (chartSummaryFlagId === flagId ? chartSummary : null)

function* getChartSummary() {
  const {
    selectedFlagId,
    chartSummary,
    chartSummaryFlagId,
    selectedFlagDetail,
  }: ReturnType<typeof getQaAddendumSelector> = yield select(getQaAddendumSelector)

  if (!selectedFlagId) return
  const flagId = String(selectedFlagId)

  if (chartSummaryFlagId === flagId && hasChartSummaryPages(chartSummary)) {
    yield put(setChartSummary(chartSummary, flagId))
    return
  }

  const prid = Number(selectedFlagDetail?.prid)
  if (!prid) return

  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const qaAddendumService = new QaAddendumService(authenticationJwt)

  let apiFailure = false

  try {
    const response: IQaChartSummaryResponse = yield call([qaAddendumService, qaAddendumService.fetchChartSummary], prid)

    if (response.status === 200 && hasChartSummaryPages(response.data ?? null)) {
      yield put(setChartSummary(response.data, flagId))
      Logger.info(`QaAddendum: getChartSummary success, flagId: ${flagId}`)
      return
    }

    if (response.status !== 200) {
      apiFailure = true
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage = i18n.t('qaFlags.qaAddendum.flagResolution.loadChartSummaryError')
      }
      yield put(handleApiError(response))
    }
  } catch (e: any) {
    if (QaAddendumService.isAbortError(e)) {
      const existingSummary = getExistingSummaryForFlag(chartSummaryFlagId, flagId, chartSummary)
      yield put(setChartSummary(existingSummary, hasChartSummaryPages(existingSummary) ? flagId : null))
      return
    }

    const cause = e.message ? e.message : 'unknown'
    apiFailure = true
    yield put(
      handleApiError(
        ZollAPIService.createToastLogErrorResponse(
          i18n.t('qaFlags.qaAddendum.flagResolution.loadChartSummaryFailed', { cause })
        )
      )
    )
  }

  const existingSummary = getExistingSummaryForFlag(chartSummaryFlagId, flagId, chartSummary)
  yield put(setChartSummary(existingSummary, hasChartSummaryPages(existingSummary) ? flagId : null))

  if (!apiFailure && !hasChartSummaryPages(existingSummary)) {
    yield put(
      showToast({
        message: i18n.t('qaFlags.qaAddendum.flagResolution.loadChartSummaryError'),
        duration: 3000,
        position: 'top',
        color: ToastColor.Error,
      })
    )
  }
}

export function* getChartSummarySaga() {
  yield takeLatest([QaAddendumConstants.FETCH_CHART_SUMMARY], performWithMutexHOG, getChartSummary)
}
